import { Vehicle } from '../../model/Quote'
import { ImacaVehicle, PaintSwatch } from '../../model/Swatches'
import { VoVehicle } from '../../model/VoVehicle'
import { ImageUrlType } from '../../pages/vehicle-configurator/VehicleGallery'
import { buildUrl } from '../urlBuilder'
import {
  createImageUrl,
  getLocaleCountry,
  getModelYear,
  getVehicleBodyType,
  getVehicleMake,
  getVehicleModelCode,
  getVehicleVersion,
  getVehicleId,
} from './vehicleImagesUtils'

const DEFAULT_ZOOM_TYPE = 'Fullscreen'
const DEFAULT_ZOOM_LEVEL = 30
const ANGLE_12 = 12
const ANGLE_16 = 16
const ANGLE_20 = 20
const ANGLE_24 = 24
const ANGLE_28 = 28
const ANGLE_32 = 32
const ANGLE_1 = 1
const ANGLE_5 = 5
const ANGLE_9 = 9
const DEFAULT_ANGLE = ANGLE_12
const GALLERY_ANGLES = [
  ANGLE_12,
  ANGLE_16,
  ANGLE_20,
  ANGLE_24,
  ANGLE_28,
  ANGLE_32,
  ANGLE_1,
  ANGLE_5,
  ANGLE_9,
]
const SHOWROOM_WIDTH = 400
const GALLERY_WIDTH = 1200
const DEFAULT_COLOUR = 'Silver'

const ensureHexPrefix = (colourHex: string): string => {
  if (!colourHex.trim().startsWith('#')) {
    return `#${colourHex}`
  }
  return colourHex
}

const buildGetImageUrl = (queryParams: Record<string, unknown>): string =>
  buildUrl({
    path: process.env.REACT_APP_GET_IMAGES_URL,
    queryParams,
  })

const getCountry = (locale: string): string => {
  const localeCountry = getLocaleCountry(locale)
  if (localeCountry === 'NO') {
    return 'N'
  } else if (localeCountry === 'IE') {
    return 'IRL'
  } else {
    return 'P'
  }
}

const getVehicleSearchParameters = (
  locale: string,
  vehicle: VoVehicle | Vehicle,
) => {
  const countryCode = getCountry(locale)
  const year = getModelYear(vehicle)
  const manufacturer = getVehicleMake(vehicle)
  const modelName = getVehicleModelCode(vehicle)
  const seriesName = getVehicleVersion(vehicle)
  const bodyType = getVehicleBodyType(vehicle)
  const vehicleId = getVehicleId(vehicle)

  return {
    manufacturer,
    modelName,
    seriesName,
    year,
    countryCode,
    bodyType,
    vehicleId,
  }
}

const getVehicleQueryParameters = (
  locale: string,
  vehicle: Vehicle,
  bodyColor?: string,
) => {
  return {
    ...getVehicleSearchParameters(locale, vehicle),
    ...(bodyColor && { paintId: bodyColor }),
    baseColour: DEFAULT_COLOUR,
    zoomType:
      process.env.REACT_APP_IMAGES_DEFAULT_ZOOM_TYPE || DEFAULT_ZOOM_TYPE,
    zoomLevel:
      process.env.REACT_APP_IMAGES_DEFAULT_ZOOM_LEVEL || DEFAULT_ZOOM_LEVEL,
  }
}

const getImacaVehicleImageGallery = (
  vehicle: Vehicle,
  locale: string,
  bodyColor?: string,
): ImageUrlType[] => {
  const defaultParameters = getVehicleQueryParameters(
    locale,
    vehicle,
    bodyColor,
  )
  return GALLERY_ANGLES.map((angle) => ({
    ...defaultParameters,
    angle,
    width: GALLERY_WIDTH,
  }))
    .map(buildGetImageUrl)
    .map(createImageUrl)
}

const getImacaVehicleImage = (vehicle: Vehicle, locale: string): string => {
  return buildGetImageUrl({
    ...getVehicleQueryParameters(locale, vehicle),
    angle: DEFAULT_ANGLE,
    width: SHOWROOM_WIDTH,
  })
}

const mapImacaSwatchesResponse = (
  imacaVehicles: ImacaVehicle[],
): PaintSwatch[] => {
  const result: PaintSwatch[] = []
  if (imacaVehicles) {
    imacaVehicles
      .filter((vehicle) => vehicle.colours)
      .forEach((vehicle) => {
        vehicle.colours
          .filter(
            (colour) => colour.colourId != null && colour.colourHex != null,
          )
          .forEach((colour) => {
            const colourComponents = colour.colourHex.split(',')
            if (colourComponents.length > 0) {
              result.push({
                paintId: colour.colourId,
                colourHexHigh: ensureHexPrefix(colourComponents[0]),
                colourHexLow: ensureHexPrefix(
                  colourComponents[colourComponents.length - 1],
                ),
              })
            }
          })
      })
  }
  return result
}

const getImacaPaintSwatchesKey = (
  vehicle: VoVehicle,
  locale: string,
  colours: string,
) =>
  getCountry(locale) +
  getVehicleMake(vehicle) +
  getVehicleModelCode(vehicle) +
  getVehicleVersion(vehicle) +
  getModelYear(vehicle) +
  colours

export {
  getImacaVehicleImage,
  getImacaVehicleImageGallery,
  getImacaPaintSwatchesKey,
  mapImacaSwatchesResponse,
  getVehicleSearchParameters,
}
